/**
 * Internal logistics costs (seller): product overrides → carrier defaults → 0.
 * Never use these for storefront order totals — only for dashboard/analytics.
 */

/** Client-facing fallback when `product.shipping_fee` is null (agence). */
export type CarrierClientShippingFallback = {
  default_shipping_fee?: number | null
} | null | undefined

export type CarrierCostFallback = {
  shipping_cost?: number | null
  return_cost?: number | null
  default_shipping_fee?: number | null
} | null | undefined

export type ProductCostOverrides = {
  delivery_cost?: number | null
  return_cost?: number | null
} | null | undefined

/** Frais livraison client: produit d’abord, sinon défaut transporteur, sinon 0. */
export function resolveClientShippingFee(
  product: { shipping_fee?: number | null } | null | undefined,
  carrier: CarrierClientShippingFallback,
): number {
  const p = product?.shipping_fee
  if (p != null) {
    const n = Number(p)
    return Number.isFinite(n) && n >= 0 ? n : 0
  }
  const c = carrier?.default_shipping_fee
  if (c != null) {
    const n = Number(c)
    return Number.isFinite(n) && n >= 0 ? n : 0
  }
  return 0
}

/** Cart line shape for checkout shipping (id or product_id; optional embedded product snapshot). */
export type CartLineForShipping = {
  id?: unknown
  product_id?: unknown
  product?: { shipping_fee?: number | null } | null
}

/**
 * One client shipping fee per order = MAX over lines (not SUM, not × quantity).
 * Each line: product.shipping_fee ?? carrier.default_shipping_fee ?? 0 (same as server checkout).
 */
export function maxClientShippingFeePerOrder(
  cartItems: ReadonlyArray<CartLineForShipping>,
  catalogProducts: ReadonlyArray<{ id: string; shipping_fee?: number | null }>,
  carrierDefaultShippingFee: number,
): number {
  if (!cartItems.length) return 0
  const fallback = Number.isFinite(carrierDefaultShippingFee) && carrierDefaultShippingFee >= 0 ? carrierDefaultShippingFee : 0
  const carrier = { default_shipping_fee: fallback }
  const lineFees = cartItems.map((item) => {
    const pid = String(item.id ?? item.product_id ?? '')
    const fromCatalog = catalogProducts.find((p) => String(p.id) === pid)
    const productFee = fromCatalog?.shipping_fee ?? item.product?.shipping_fee
    return resolveClientShippingFee({ shipping_fee: productFee }, carrier)
  })
  return lineFees.length ? Math.max(...lineFees, 0) : 0
}

export function resolveDeliveryCost(
  product: ProductCostOverrides,
  carrier: CarrierCostFallback,
): number {
  const o = product?.delivery_cost
  if (o != null) {
    const n = Number(o)
    return Number.isFinite(n) && n >= 0 ? n : 0
  }
  const c = carrier?.shipping_cost
  if (c != null) {
    const n = Number(c)
    return Number.isFinite(n) && n >= 0 ? n : 0
  }
  return 0
}

export function resolveReturnCost(product: ProductCostOverrides, carrier: CarrierCostFallback): number {
  const o = product?.return_cost
  if (o != null) {
    const n = Number(o)
    return Number.isFinite(n) && n >= 0 ? n : 0
  }
  const c = carrier?.return_cost
  if (c != null) {
    const n = Number(c)
    return Number.isFinite(n) && n >= 0 ? n : 0
  }
  return 0
}

/** Remove internal-only columns before exposing product rows on the public storefront. */
export function stripInternalProductShippingFields<T extends Record<string, unknown>>(row: T): T {
  const { delivery_cost: _d, return_cost: _r, ...rest } = row
  return rest as T
}

export function parseOptionalNonNegativeTnd(
  input: string,
): { ok: true; value: number | null } | { ok: false } {
  const t = input.trim()
  if (t === '') return { ok: true, value: null }
  const n = Number(t.replace(',', '.'))
  if (!Number.isFinite(n) || n < 0) return { ok: false }
  return { ok: true, value: n }
}
